import { queueTrackingEvent } from '@library/tracking-integration'

export enum TrackingActions {
  CLICK_GET_STARTED = 'click_get_started',
  CUSTOMER_REVIEWS_TRUSTPILOT_CTA = 'click_value_comprehension_trustpilot_cta',
  HOMEPAGE_REVIEWS_TRUSTPILOT_CTA = 'click_homepage_reviews_trustpilot_cta',
  OUR_SUPPLIERS = 'click_value_comprehension_supplier_cta',
  OUR_SUPPLIERS_CAROUSEL = 'click_value_comprehension_supplier_carousel',
  CLICK_PLAY_VIDEO = 'click_play_video',
  CLICK_PAUSE_VIDEO = 'click_pause_video',
  // Event name per the CwC analytics spec sheet (deliberately not snake_case, unlike the others).
  HOW_GOUSTO_WORKS_CLICKED = 'HowGoustoWorks Clicked',
  // Existing analytics event, shared with the burger-menu "box prices" nav link — this is a new
  // firing location, not a new event. Deliberately not snake_case, matching HOW_GOUSTO_WORKS_CLICKED.
  BOX_PRICING_NAVIGATION = 'BoxPricingNavigation Clicked',
  THIS_WEEKS_MENU = 'ThisWeeksMenu Clicked',
}

export enum LandingPageContentSections {
  CUSTOMER_REVIEWS = 'customerReviews',
  HOMEPAGE_REVIEWS = 'homepageReviews',
  EFFORTLESS_HOME_COOKING = 'effortlessHomeCooking',
  VALUE_YOU_CAN_TASTE = 'valueYouCanTaste',
  ENDLESS_VARIETY = 'endlessVariety',
  OUR_SUPPLIERS = 'ourSuppliers',
  // snake_case per the squad's analytics spec for this event (differs from the camelCase above).
  WHATS_NEW = 'whats_new',
  HOW_RECIPE_BOXES_WORK = 'how_recipe_boxes_work',
  REVIEWS = 'reviews',
  VALUE = 'value',
  FLOATING = 'floating',
  WEEKLY_COLLECTIONS = 'weekly_collections',
}

type CTAClickTrackingData = {
  action: TrackingActions
  properties: {
    section: LandingPageContentSections
    url: string
    [key: string]: unknown
  }
}

type CarouselSlideChangeTrackingData = {
  action: 'carousel_slide_change'
  properties: {
    prevSlideIndex: number
    currentSlideIndex: number
    [key: string]: unknown
  }
}

export function trackCTAClick(
  action: TrackingActions,
  landingPageContentSections: LandingPageContentSections,
  extra: Record<string, unknown> = {},
) {
  queueTrackingEvent<CTAClickTrackingData>({
    action,
    properties: {
      section: landingPageContentSections,
      url: window.location.href,
      ...extra,
    },
  })
}

type VideoPlaybackTrackingData = {
  action: TrackingActions
  properties: {
    section: LandingPageContentSections
    tile: string
    [key: string]: unknown
  }
}

/**
 * Fires a play/pause video tracking event. Unlike `trackCTAClick`, it carries a `section` + `tile`
 * and deliberately **no `url`** — these events represent in-page video control interactions, not
 * navigation.
 */
export function trackVideoPlayback(
  action: TrackingActions,
  section: LandingPageContentSections,
  tile: string,
) {
  queueTrackingEvent<VideoPlaybackTrackingData>({
    action,
    properties: {
      section,
      tile,
    },
  })
}

export function trackCarouselSlideChange(
  prevSlideIndex: number,
  currentSlideIndex: number,
  snowplowTrackingProperties: Record<string, unknown> = {},
) {
  queueTrackingEvent<CarouselSlideChangeTrackingData>({
    action: 'carousel_slide_change',
    properties: {
      prevSlideIndex,
      currentSlideIndex,
      ...snowplowTrackingProperties,
    },
  })
}
